/**
 * @file index.tsx
 *
 * @fileoverview An action boxed in a Box component together with an icon.
 * The component has 2 "slots", content and action.
 */

import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import Button from "../../../02-atoms/01-forms/01-button";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import Box from "../../../02-atoms/07-box";
import styles from "./boxAction.module.scss";

const box = cva([styles.box], {
  variants: { disabled: { true: styles.boxDisabled } },
  defaultVariants: { disabled: false },
});
const wrapButton = cva([styles.wrapButton], {
  variants: {
    disabled: {
      true: styles.wrapButtonDisabled,
    },
  },
});
const iconCva = cva([styles.icon], {
  variants: {
    disabled: {
      true: styles.iconDisabled,
    },
  },
});

export interface BoxActionProps extends VariantProps<typeof box> {
  /**
   * Icon to show on the box.
   */
  icon?: ReactNode;
  /**
   * In case of a custom button you can insert it here.
   */
  button?: ReactNode;
  /**
   * Whole card as a button.
   *
   * @default "false".
   */
  asButton?: boolean;
  /**
   * Color of the icon.
   */
  iconColor?: string;
  /**
   * OnClick even handler.
   */
  onClick?: any;
  /**
   * Content to show inside the box (text).
   */
  children: ReactNode;
  /**
   * The call to action text.
   */
  actionText?: ReactNode;
}

/**
 * A box action can be used to show contents and a call to action inside a box. Useful for empty
 * panes and other call to action boxes.
 */
export default function BoxAction({
  onClick,
  icon,
  button,
  iconColor = "gray2",
  children,
  asButton = false,
  actionText,
  disabled = false,
}: BoxActionProps) {
  const actionButtonComp = (
    <Box className={box({ disabled })} data-testid="box-action">
      <span
        className={iconCva({ disabled })}
        {...(iconColor && {
          style: {
            color: `var(--color-${iconColor})`,
          },
        })}
      >
        {icon}
      </span>
      <Spacing size="small" />
      {children}
      <Spacing size="medium" />
      <div>
        {!button && actionText ? (
          <Button
            {...(onClick && !disabled ? { onClick } : null)}
            buttonStyle="primary"
            disabled={disabled}
          >
            {actionText}
          </Button>
        ) : (
          button
        )}
      </div>
    </Box>
  );

  return asButton ? (
    <button
      className={wrapButton({ disabled })}
      {...(onClick && !disabled ? { onClick } : null)}
    >
      {actionButtonComp}
    </button>
  ) : (
    actionButtonComp
  );
}
export { BoxAction };
